import React from 'react';
import { BrowserRouter as Router, Navigate, Routes, Route, useLocation } from 'react-router';
import EngineAccessGuard from './components/EngineAccessGuard';
import Home from './components/Home';
import UploadRouteGuard from './components/UploadRouteGuard';
import LscRouteGuard from './components/LscRouteGuard';
import Analyzing from './components/Analyzing';
import Report from './components/Report';
import Profile from './components/Profile';
import AdminReviewQueue from './pages/AdminReviewQueue';
import Navbar from './components/Navbar';
import { AnalysisProvider } from './contexts/AnalysisContext';
import { UserProvider } from './contexts/UserContext';
import LscApp from './lsc/LscApp';
import { DocumentHeadSync } from './lsc/DocumentHeadSync';
import { isStreamCenterHost } from './lsc/routing';
import { isLscAdminOnly, isLscOnlyMode, isRttsStudioEnabled } from './config/featureFlags';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import LegalFooter from './components/LegalFooter';
import TermsAcceptanceGate from './components/TermsAcceptanceGate';
import StudioApp from './studio/StudioApp';
import PublicReelWatchPage from './studio/pages/PublicReelWatchPage';
import LscMemberInviteClaimPage from './lsc/pages/LscMemberInviteClaimPage';

function MainAppShell() {
  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white">
      <Navbar />
      <main className="flex-1 container mx-auto px-4 py-8">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/upload" element={<UploadRouteGuard />} />
          <Route
            path="/analyzing"
            element={
              <EngineAccessGuard>
                <Analyzing />
              </EngineAccessGuard>
            }
          />
          <Route
            path="/report"
            element={
              <EngineAccessGuard>
                <Report />
              </EngineAccessGuard>
            }
          />
          <Route path="/profile" element={<Profile />} />
          <Route path="/admin/review" element={<AdminReviewQueue />} />
          <Route
            path="/report/:id"
            element={
              <EngineAccessGuard>
                <Report />
              </EngineAccessGuard>
            }
          />
        </Routes>
      </main>
      <LegalFooter />
    </div>
  );
}

/**
 * App route tree. When LSC_ONLY_MODE is on, FI redirects must be <Route> elements
 * (or a Fragment of them) as *direct* children of <Routes>. Wrapping them in a
 * custom component is rejected by React Router ("X is not a <Route> component")
 * and blanks the entire app — that was the production cutover incident.
 */
export function AppRoutes() {
  const onStreamHost =
    typeof window !== 'undefined' && isStreamCenterHost(window.location.hostname);
  const lscOnly = isLscOnlyMode();
  const lscAdminOnly = isLscAdminOnly();
  const serveLscAtRoot = onStreamHost || lscOnly;

  const studioEnabled = isRttsStudioEnabled();
  const studioAsLanding = lscAdminOnly && studioEnabled;

  const lscSurface = lscAdminOnly ? (
    <LscRouteGuard>
      <LscApp />
    </LscRouteGuard>
  ) : (
    <LscApp />
  );

  return (
    <Routes>
      <Route path="/r/:token" element={<PublicReelWatchPage />} />
      <Route path="/lsc/claim/:token" element={<LscMemberInviteClaimPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/terms/" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/privacy/" element={<PrivacyPage />} />
      {studioEnabled ? <Route path="/studio/*" element={<StudioApp />} /> : null}
      {studioAsLanding ? <Route path="/" element={<Navigate to="/studio" replace />} /> : null}
      {serveLscAtRoot ? (
        <>
          {lscOnly ? (
            <>
              <Route path="/upload" element={<Navigate to="/" replace />} />
              <Route path="/analyzing" element={<Navigate to="/" replace />} />
              <Route path="/report" element={<Navigate to="/" replace />} />
              <Route path="/report/:id" element={<Navigate to="/" replace />} />
              <Route path="/profile" element={<Navigate to="/settings" replace />} />
              <Route path="/admin/review" element={<Navigate to="/" replace />} />
            </>
          ) : null}
          {lscAdminOnly ? <Route path="/lsc/*" element={lscSurface} /> : null}
          <Route path="/*" element={lscSurface} />
        </>
      ) : (
        <>
          <Route path="/lsc/*" element={lscSurface} />
          <Route path="/*" element={<MainAppShell />} />
        </>
      )}
    </Routes>
  );
}

function GatedAppRoutes() {
  const { pathname } = useLocation();
  if (pathname.startsWith('/r/')) {
    return <AppRoutes />;
  }
  return (
    <TermsAcceptanceGate>
      <AppRoutes />
    </TermsAcceptanceGate>
  );
}

function App() {
  return (
    <UserProvider>
      <AnalysisProvider>
        <Router>
          <DocumentHeadSync />
          <GatedAppRoutes />
        </Router>
      </AnalysisProvider>
    </UserProvider>
  );
}

export default App;
